import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, Play, ThumbsUp, MessageSquare, Youtube, TrendingUp, ArrowRight, Music, Video, ArrowLeft,
} from "lucide-react";
import Lenis from "lenis";
import { API, mediaUrl } from "@/lib/api";
import { LogoMark } from "@/components/brand/Logo";
import { goToApp } from "@/lib/appUrl";

const fmt = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}K`;
  return v.toLocaleString();
};

const easeOut = [0.22, 1, 0.36, 1];

export default function Trending() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Lenis smooth scroll (matches Landing)
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true, wheelMultiplier: 1 });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/public/trending`);
        if (res.ok) setData(await res.json());
      } catch {
        // Empty-state design handles fallback
      } finally {
        setLoading(false);
      }
    })();

    // SEO — set title + description on mount (App-level meta already covers the base tags)
    const prevTitle = document.title;
    document.title = "Now Playing on Noizbloc — What the world's listening to";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", "Discover trending music and top-performing videos from artists distributing on Noizbloc. Real-time views across YouTube for indie artists and labels.");
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, []);

  const totals = data?.totals || {};
  const topVideos = data?.top_videos || [];
  const topReleases = data?.top_releases || [];

  return (
    <div className="relative bg-[#020617] text-white nz-grain min-h-screen" data-testid="trending-page">
      {/* Ambient background — subtle red-to-blue gradient hint */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden z-0">
        <div className="absolute -top-40 -right-24 h-[520px] w-[520px] rounded-full bg-red-500/[0.06] blur-[120px]" />
        <div className="absolute top-[40%] -left-32 h-[420px] w-[420px] rounded-full bg-blue-500/[0.08] blur-[130px]" />
      </div>

      {/* Minimal nav */}
      <header className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="trending-brand" className="flex items-center gap-2.5 group">
          <LogoMark className="text-white group-hover:scale-110 transition-transform" />
          <span className="font-display text-xl font-semibold tracking-tight text-white">Noizbloc</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link
            to="/"
            data-testid="trending-back-home"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={14} /> Home
          </Link>
          <button
            data-testid="trending-signin"
            onClick={() => { if (!goToApp("/login")) navigate("/login"); }}
            className="hidden sm:inline-flex items-center text-sm font-medium text-slate-300 hover:text-white px-3 py-2.5 transition-colors"
          >
            Sign in
          </button>
          <button
            data-testid="trending-cta-nav"
            onClick={() => { if (!goToApp("/register")) navigate("/register"); }}
            className="inline-flex items-center rounded-full bg-white text-black text-sm font-medium px-5 py-2.5 hover:bg-slate-200 active:scale-[0.98] transition-all"
          >
            Distribute your music
          </button>
        </div>
      </header>

      <main className="relative z-[2]">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 md:pt-20 pb-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: easeOut }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase text-red-300 border border-red-500/25 bg-red-500/[0.06] rounded-full px-3 py-1.5">
              <Youtube size={12} /> Now Playing on Noizbloc
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white">
              What the world is <span className="text-slate-500">listening to</span>
              <span className="text-white">.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed">
              Real-time views across YouTube for every artist and label distributing through Noizbloc.
              Updated every 15 minutes, ranked by actual listener plays — not paid placement.
            </p>
          </motion.div>

          {/* Platform stat row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: easeOut, delay: 0.15 }}
            className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5"
            data-testid="trending-stats"
          >
            {[
              { label: "Total views", value: fmt(totals.views), icon: Play },
              { label: "Total likes", value: fmt(totals.likes), icon: ThumbsUp },
              { label: "Videos matched", value: fmt(data?.videos_matched), icon: Video },
              { label: "Live releases", value: fmt(data?.live_count), icon: Music },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 md:p-5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  <Icon size={12} /> {label}
                </div>
                <div className="mt-2 font-display text-2xl md:text-3xl tracking-tight text-white">{loading ? "—" : value}</div>
              </div>
            ))}
          </motion.div>
        </section>

        {/* Top videos */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-6 pb-16">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              <TrendingUp size={18} className="text-red-300" />
              <h2 className="font-display text-xl md:text-2xl tracking-tight text-white">Top performing videos</h2>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold hidden sm:inline">
              Real-time · YouTube Data API
            </span>
          </div>

          {loading ? (
            <div className="py-20 flex justify-center" data-testid="trending-loading">
              <Loader2 className="animate-spin text-slate-500" />
            </div>
          ) : topVideos.length === 0 ? (
            <EmptyStateHero navigate={navigate} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-testid="trending-videos-grid">
              {topVideos.map((v, i) => (
                <motion.a
                  key={v.video_id}
                  href={`https://youtube.com/watch?v=${v.video_id}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`trending-video-${v.video_id}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: easeOut, delay: Math.min(i, 8) * 0.04 }}
                  className="group relative rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden hover:border-white/25 hover:shadow-[0_20px_50px_-25px_rgba(239,68,68,0.35)] transition-[border-color,box-shadow] duration-300"
                >
                  <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-white/95 bg-black/60 backdrop-blur-md rounded-full px-2 py-1">
                    #{i + 1}
                  </div>
                  <div className="relative aspect-video overflow-hidden bg-black">
                    {v.thumbnail ? (
                      <img
                        src={v.thumbnail}
                        alt={v.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-500">
                        <Video size={32} />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="h-14 w-14 rounded-full bg-red-600 flex items-center justify-center shadow-2xl">
                        <Play size={22} className="text-white ml-1" fill="currentColor" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="text-sm text-white line-clamp-2 min-h-[2.5rem] group-hover:text-white transition-colors">
                      {v.title}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 truncate">
                      {v.display_artist || v.channel_title}
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Play size={12} className="text-slate-500" /> {fmt(v.views)}</span>
                      <span className="flex items-center gap-1"><ThumbsUp size={12} className="text-slate-500" /> {fmt(v.likes)}</span>
                      <span className="flex items-center gap-1"><MessageSquare size={12} className="text-slate-500" /> {fmt(v.comments)}</span>
                    </div>
                    <div className="mt-2 text-[10px] text-slate-500 uppercase tracking-widest truncate">
                      from {v.release_title}
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
          )}
        </section>

        {/* Top releases sidebar section */}
        {topReleases.length > 0 && (
          <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-16">
            <div className="flex items-center gap-2 mb-6">
              <Music size={18} className="text-blue-300" />
              <h2 className="font-display text-xl md:text-2xl tracking-tight text-white">Top releases this week</h2>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden divide-y divide-white/5" data-testid="trending-releases">
              {topReleases.map((r, i) => (
                <motion.div
                  key={r.release_id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: easeOut, delay: i * 0.05 }}
                  className="flex items-center gap-4 px-5 md:px-6 py-4"
                  data-testid={`trending-release-${r.release_id}`}
                >
                  <div className="w-6 text-slate-600 text-sm font-display">{i + 1}</div>
                  <div className="h-14 w-14 shrink-0 rounded-xl overflow-hidden bg-gradient-to-br from-blue-900/40 to-[#040a1a] flex items-center justify-center">
                    {r.cover_url ? (
                      <img src={mediaUrl(r.cover_url)} alt={r.title} loading="lazy" className="h-full w-full object-cover" />
                    ) : (
                      <Music size={18} className="text-blue-400/50" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-medium truncate">{r.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      {r.display_artist || "Independent artist"} · {r.type} · {r.video_count} video{r.video_count === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-white font-display text-lg tracking-tight">{fmt(r.views)}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest">views</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, ease: easeOut }}
            className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.06] via-white/[0.02] to-transparent p-8 md:p-12"
          >
            <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-red-500/[0.10] blur-3xl" />
            <div className="absolute -left-12 -bottom-16 h-64 w-64 rounded-full bg-blue-500/[0.10] blur-3xl" />
            <div className="relative max-w-2xl">
              <div className="inline-block text-[10px] font-bold tracking-[0.25em] uppercase text-slate-500 border border-white/10 rounded-full px-3 py-1.5">
                Your turn
              </div>
              <h2 className="mt-5 font-display text-3xl md:text-5xl tracking-tight text-white">
                Get your song heard.
              </h2>
              <p className="mt-4 text-base md:text-lg text-slate-400 max-w-lg">
                Join independent artists and labels distributing to 150+ stores worldwide. Keep 100% of your royalties. See real-time performance on the same board you're browsing now.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button
                  data-testid="trending-cta-register"
                  onClick={() => { if (!goToApp("/register")) navigate("/register"); }}
                  className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-6 py-3 hover:bg-slate-200 active:scale-[0.98] transition-all"
                >
                  Start distributing <ArrowRight size={16} />
                </button>
                <Link
                  to="/"
                  data-testid="trending-cta-learn"
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 text-white font-medium px-6 py-3 hover:bg-white/5 transition-colors"
                >
                  Learn more
                </Link>
              </div>
            </div>
          </motion.div>
        </section>
      </main>

      {/* Compact footer */}
      <footer className="relative z-[2] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 flex items-center justify-between flex-wrap gap-4">
          <div className="text-xs text-slate-500">
            © {new Date().getFullYear()} Noizbloc Private Limited. Get your song heard.
          </div>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/" className="text-slate-500 hover:text-white transition-colors">Home</Link>
            <a href="mailto:support@noizbloc.com" className="text-slate-500 hover:text-white transition-colors">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function EmptyStateHero({ navigate }) {
  return (
    <div
      data-testid="trending-empty"
      className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-10 md:p-16 text-center"
    >
      <div className="mx-auto h-14 w-14 rounded-2xl bg-red-500/10 border border-red-500/25 flex items-center justify-center">
        <Youtube size={22} className="text-red-300" />
      </div>
      <h3 className="mt-5 font-display text-2xl tracking-tight text-white">The board's warming up</h3>
      <p className="mt-2 text-slate-400 max-w-md mx-auto">
        No matched videos yet. Once artists on Noizbloc go live, their real-time YouTube performance shows up here.
      </p>
      <button
        onClick={() => { if (!goToApp("/register")) navigate("/register"); }}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-medium px-5 py-2.5 hover:bg-slate-200"
      >
        Be one of the first <ArrowRight size={14} />
      </button>
    </div>
  );
}
